import React from "react";
import {AppApi} from "../../../utils/AppApi";
import {useAlert} from "../../../utils/components/providers/AlertProvider";
import {Utils} from "../../../utils/Utils";

interface Member {
    selected: boolean
    member_id: number,
    email: string
    contacts: string[]
    hotel_name: string
    first_name: string
    last_name: string
}

export default function SendMessage(params: { type: 'SMS' | 'Email' }) {
    const {showMessage} = useAlert()

    const [members, setMembers] = React.useState<Member[]>([]);

    const getMembers = () => {
        Utils.toggleSpinner('show')
        AppApi.initiateMessaging()
            .then(res => {
                Utils.toggleSpinner('hide')
                if (res.data.success) {
                    setMembers((res.data.members as any[]).map(function (aData) {
                        return {
                            member_id : aData.member_id,
                            email     : aData.email,
                            contacts  : aData.contacts,
                            hotel_name: aData.hotel_name,
                            first_name: aData.first_name,
                            last_name : aData.last_name,
                            selected  : false
                        };
                    }))
                }
            })
            .catch(() => {
                Utils.toggleSpinner('hide')
                showMessage({message: 'Error while loading members', type: 'error'})
            })
    }

    const [sms, setSMS] = React.useState<{ show: boolean, message: string }>({show: false, message: ''})
    const sendSMS = () => {
        const contacts = members
            .filter(member => member.contacts.length > 0)
            .filter((member) => member.selected)
            .map(member => member.contacts.join(","))
            .join(",")
            .split(",")
            .map(contact => `+${contact}`)

        if (contacts.length === 0) {
            showMessage({message: 'No contacts have been selected', type: 'error'})
        } else if (sms.message.length < 5) {
            showMessage({message: 'Enter a valid SMS message', type: 'error'})
        } else {
            Utils.toggleSpinner('show')
            AppApi.sendSMS({message: sms.message.trim(), selectedNumbers: JSON.stringify(contacts),})
                .then(res => {
                    Utils.toggleSpinner('hide')
                    if (res.data.success) {
                        setSMS({...sms, show: false})
                        showMessage({message: 'SMSs have been sent successfully', type: 'success'})
                    } else {
                        showMessage({message: 'Error while sending SMSs, please try again', type: 'error'})
                    }
                })
                .catch(() => {
                    Utils.toggleSpinner('hide')
                    showMessage({message: 'Error while sending SMSs, please try again', type: 'error'})
                })
        }
    }

    const [email, setEmail] = React.useState<{ show: boolean, message: string, subject: string }>({
        message: "", show: false, subject: ""
    })
    const sendEmail = () => {
        const emails = members.filter(member => member.selected)
            .map(member => member.email)

        if (emails.length === 0) {
            showMessage({message: 'No emails have been selected', type: 'error'})
        } else if (email.subject.length < 3) {
            showMessage({message: 'Enter a valid email subject', type: 'error'})
        } else if (email.message.length < 10) {
            showMessage({message: 'Enter a valid email message', type: 'error'})
        } else {
            Utils.toggleSpinner('show')
            AppApi.sendEmail({
                    message       : email.message.trim(),
                    selectedEmails: JSON.stringify(emails),
                    subject       : email.subject.trim()
                })
                .then(res => {
                    Utils.toggleSpinner('hide')
                    if (res.data.success) {
                        setEmail({...email, show: false})
                        showMessage({message: 'Emails have been sent successfully', type: 'success'})
                    } else {
                        showMessage({message: 'Error while sending email, please try again', type: 'error'})
                    }
                })
                .catch(() => {
                    Utils.toggleSpinner('hide')
                    showMessage({message: 'Error while sending email, please try again', type: 'error'})
                })
        }
    }

    React.useEffect(() => {
        if (members.length === 0) {
            getMembers()
        }
    }, [params.type])

    return (
        <>
            <div className={'action_container'}>
                <span className={'action_btn'}
                      onClick={() => {
                          if (params.type === 'Email') {
                              setEmail({...email, show: true})
                          } else if (params.type === 'SMS') {
                              setSMS({...sms, show: true})
                          }
                      }}>
                    Compose {params.type}
                </span>

                <span className={'action_btn'} onClick={getMembers}>Load Members</span>
            </div>

            <div className="table-responsive table_container">
                <table className="table table-striped table-bordered" style={{minWidth: '1200px'}}>
                    <thead>
                        <tr>
                            <th style={{width: '10%', textAlign: 'center'}}>Add</th>
                            <th style={{width: '25%'}}>Hotel Name</th>
                            <th style={{width: '25%'}}>Full Name</th>
                            {params.type === 'Email' && <th style={{width: '40%'}}>Email Address</th>}
                            {params.type === 'SMS' && <th style={{width: '40%'}}>Phone Numbers</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {
                            members
                                .filter((member) => {
                                    if (params.type === 'SMS') {
                                        return member.contacts.length > 0
                                    }
                                    return true
                                })
                                .map((member) =>
                                    <tr key={member.member_id}>
                                        <td style={{textAlign: 'center'}}>
                                            <input className="form-check-input" type="checkbox" value=""
                                                   onChange={(event) => {
                                                       setMembers(members.map(aMember => aMember.member_id === member.member_id ?
                                                           {...aMember, selected: event.target.checked} : aMember))
                                                   }}
                                                   id={`check_${member.member_id}`} checked={member.selected}/>
                                        </td>
                                        <td dangerouslySetInnerHTML={{__html: `${member.first_name} ${member.last_name}`.trim()}}/>
                                        <td>{member.hotel_name}</td>
                                        {params.type === 'Email' && <td>{member.email}</td>}
                                        {
                                            params.type === 'SMS' &&
                                            <td>
                                                {member.contacts.map(contact => `+${contact}`).join((", "))}
                                            </td>
                                        }
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>

            <div className={`modal_container ${email.show ? 'show' : ''}`.trim()}>
                <div className="modal_body">
                    <h6 className="mb-4">{'Compose Email'}</h6>

                    <form method="post">
                        <div className="mb-3">
                            <label htmlFor="email_subject" className="form-label">Email Subject</label>
                            <input
                                onChange={event => setEmail({...email, subject: event.target.value})}
                                type="text" className="form-control" id="email_subject"
                                name="email_subject" value={email.subject} required/>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email_message">Email Message</label>
                            <textarea
                                onChange={event => setEmail({...email, message: event.target.value})}
                                className="form-control" name="email_message" rows={5} required value={email.message}/>
                        </div>
                    </form>

                    <div className={'form_buttons mt-3'}>
                        <div className={'row m-0'}>
                            <div className={'col-6 ps-0 pe-1'}>
                                <button type="button" className="btn btn-secondary"
                                        onClick={() => setEmail({...email, show: false})}>Close Window
                                </button>
                            </div>
                            <div className={'col-6 ps-1 pe-0'}>
                                <button type="button" className="btn btn-primary" onClick={sendEmail}>Send Email</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`modal_container ${sms.show ? 'show' : ''}`.trim()}>
                <div className="modal_body">
                    <h6 className="mb-4">{'Compose SMS'}</h6>

                    <form method="post">
                        <div className="form-group">
                            <label htmlFor="sms_message">SMS Message</label>
                            <textarea
                                onChange={event => setSMS({...sms, message: event.target.value})}
                                className="form-control" name="sms_message" rows={5} required value={sms.message}/>
                        </div>
                    </form>

                    <div className={'form_buttons mt-3'}>
                        <div className={'row m-0'}>
                            <div className={'col-6 ps-0 pe-1'}>
                                <button type="button" className="btn btn-secondary"
                                        onClick={() => setSMS({...sms, show: false})}>Close Window
                                </button>
                            </div>
                            <div className={'col-6 ps-1 pe-0'}>
                                <button type="button" className="btn btn-primary" onClick={sendSMS}>Send SMS</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}